/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useState } from 'react'
import { vercelSplit } from '@web/cms'
import AgendaPanel from 'components/materia/conference-agenda/agenda-panel/agenda-panel'
import { type ITrack } from 'app/[locale]/conferences/[conference]/agenda/[date]/page'
import s from './style.module.css'

const filterRowsBySubtrack = (
	currentSubtrack: string,
	startTimes: ITrack['startTimes'],
) => {
	return startTimes.reduce((acc, row) => {
		const matchingSessions = row.sessions.filter((session) => {
			let subtrack

			if (session.__typename === 'ConferenceSessionRecord') {
				subtrack = session?.confSubtrack?.name
			}

			const matchingSubtrack = subtrack
				? vercelSplit(subtrack) === currentSubtrack
				: false

			// Only return sessions that have a matching subtrack or no
			// subtrack.
			if (!subtrack || matchingSubtrack) {
				return true
			}
		})

		if (matchingSessions.length) {
			acc.push({
				...row,
				sessions: matchingSessions,
			})
		}

		return acc
	}, [])
}

const AgendaPanelWrapper = ({ track }: { track: ITrack }) => {
	const { startTimes, subtracks } = track
	const [currentSubtrack, setCurrentSubtrack] = useState<string | null>(
		subtracks[0] ? subtracks[0] : null,
	)
	const hasMultipleSubtracks = subtracks.length > 1
	// In the case where a user is mobile and there are multiple subtracks present,
	// we need to filter the sessions by subtrack
	const filteredRowsBySubtrack = hasMultipleSubtracks
		? filterRowsBySubtrack(currentSubtrack, startTimes)
		: startTimes

	return (
		<>
			<div className={s.isMobile}>
				<AgendaPanel
					currentSubtrack={currentSubtrack}
					subtracks={subtracks}
					rows={filteredRowsBySubtrack}
					handleDropdownChange={setCurrentSubtrack}
					showDropdown={hasMultipleSubtracks}
				/>
			</div>
			<div className={s.isDesktop}>
				<AgendaPanel
					currentSubtrack={currentSubtrack}
					subtracks={subtracks}
					// Desktop shows all sessions regardless of
					// whether or not there are subtracks
					rows={startTimes}
					handleDropdownChange={setCurrentSubtrack}
				/>
			</div>
		</>
	)
}

export default AgendaPanelWrapper
